import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, from, zip } from 'rxjs';
import { exhaustMap, switchMap, take, tap } from 'rxjs/operators';
import { CheckoutStep } from '@arc/unified-payments-frontend/shared-payment';
import { PaymentFooterActions } from '@arc/unified-payments-frontend/payment-footer';
import {
  selectIsBuyerOriginApp,
  selectIsPaymentLinks,
  selectSessionId,
  selectVirtualOtherwisePrimaryTenantId,
} from '../session-store';

/**
 * Factory that creates a reusable NgRx functional effect navigating a receipt-landing page
 * (e.g. Wallet's "Back" button) in-app to the flow's root checkout route, and setting
 * checkoutStep to Confirmation — once BOTH the payment preview and payment rules loads have
 * settled (success or error), so the reused confirmation container never mounts before its
 * data is ready. Gated on those two actions settling, not on store state, so this can't fire
 * again during the normal live checkout flow.
 *
 * Flows differ on *ordering*: POI has no per-step route/guard, so checkoutStep is set only
 * once navigation resolves. POA/HPP gate their confirmation route behind
 * `stepGuard(step, redirectRoute)` (payment-footer/src/guards/step.guard.ts), which redirects
 * away unless checkoutStep already matches — so those flows need the step set *before*
 * navigating, via `setConfirmationStepBeforeNavigating: true`.
 *
 * The root path prefix (/buyercheckout, /checkout, or /paymentlinks) is resolved here from
 * session state, not by the caller — each flow only supplies the path segment(s) after
 * :tenantId/:sessionId via `urlSuffix` (e.g. POA appends '/payonaccount/summary').
 *
 * Usage (POI, POA, international — each passes its own action set and URL suffix):
 *
 *   export const poiReceiptLandingConfirmationNavigationEffect =
 *     createReceiptLandingConfirmationNavigationEffect({
 *       loadPaymentPreviewSuccessAction: PoiPaymentReceiptComponentActions.loadPaymentPreviewSuccess,
 *       loadPaymentPreviewErrorAction: PoiPaymentReceiptComponentActions.loadPaymentPreviewError,
 *       loadPaymentRulesSuccessAction: PoiPaymentReceiptComponentActions.loadPaymentRulesSuccess,
 *       loadPaymentRulesErrorAction: PoiPaymentReceiptComponentActions.loadPaymentRulesError,
 *     });
 *
 *   export const poaReceiptLandingConfirmationNavigationEffect =
 *     createReceiptLandingConfirmationNavigationEffect({
 *       ...
 *       urlSuffix: '/payonaccount/summary',
 *       setConfirmationStepBeforeNavigating: true,
 *     });
 */
export function createReceiptLandingConfirmationNavigationEffect(config: {
  loadPaymentPreviewSuccessAction: ActionCreator<string, Creator<any[], Action>>;
  loadPaymentPreviewErrorAction: ActionCreator<string, Creator<any[], Action>>;
  loadPaymentRulesSuccessAction: ActionCreator<string, Creator<any[], Action>>;
  loadPaymentRulesErrorAction: ActionCreator<string, Creator<any[], Action>>;
  urlSuffix?: string;
  setConfirmationStepBeforeNavigating?: boolean;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      router = inject(Router),
    ) => {
      const previewSettled$ = actions$.pipe(
        ofType(
          config.loadPaymentPreviewSuccessAction,
          config.loadPaymentPreviewErrorAction,
        ),
        take(1),
      );
      const rulesSettled$ = actions$.pipe(
        ofType(
          config.loadPaymentRulesSuccessAction,
          config.loadPaymentRulesErrorAction,
        ),
        take(1),
      );

      const setConfirmationStep = () =>
        store.dispatch(
          PaymentFooterActions.CurrentCheckoutStepChangedAction({
            payload: { currentStep: CheckoutStep.Confirmation },
          }),
        );

      const buildRootUrl = (
        tenantId: string,
        sessionId: string,
        isBuyerOriginApp: boolean,
        isPaymentLinks: boolean,
      ) => {
        const prefix = isPaymentLinks
          ? 'paymentlinks'
          : isBuyerOriginApp
            ? 'buyercheckout'
            : 'checkout';
        return `/${prefix}/${tenantId}/${sessionId}${config.urlSuffix ?? ''}`;
      };

      return zip(previewSettled$, rulesSettled$).pipe(
        concatLatestFrom(() => [
          store.select(selectVirtualOtherwisePrimaryTenantId),
          store.select(selectSessionId),
          store.select(selectIsBuyerOriginApp),
          store.select(selectIsPaymentLinks),
        ]),
        exhaustMap(([, tenantId, sessionId, isBuyerOriginApp, isPaymentLinks]) => {
          const navigate = () =>
            router.navigateByUrl(
              buildRootUrl(tenantId, sessionId, isBuyerOriginApp, isPaymentLinks),
            );

          if (config.setConfirmationStepBeforeNavigating) {
            setConfirmationStep();
            return from(navigate());
          }

          return from(navigate()).pipe(tap(() => setConfirmationStep()));
        }),
        switchMap(() => EMPTY),
      );
    },
    { functional: true, dispatch: false },
  );
}
